import type {
  AfterViewInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  QueryList,
  Renderer2,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { IconSize } from '@freelancer/ui/icon';
import { PictureLayout, PictureResizeFit } from '@freelancer/ui/picture';
import type { Subscription } from 'rxjs';
import type { ThumbnailViewerImage } from './thumbnail-viewer.types';
import {
  ThumbnailViewerArrowPosition,
  ThumbnailViewerSlidesToScroll,
} from './thumbnail-viewer.types';

@Component({
  standalone: false,
  selector: 'fl-thumbnail-viewer',
  template: `
    @if (arrows) {
      <fl-link
        class="Navigator Navigator--prev"
        [class.IsHidden]="hidePrevArrow"
        (click)="onViewPrevious($event)"
      >
        <fl-icon
          i18n-label="Thumbnail viewer icon to view previous"
          label="Previous"
          [name]="'ui-arrow-left-alt'"
          [size]="IconSize.SMALL"
        ></fl-icon>
      </fl-link>
    }
    <div class="Slider">
      <div
        #track
        class="Slider-track"
      >
        @for (
          image of images;
          track trackByImageSource(slideItemIndex, image);
          let slideItemIndex = $index
        ) {
          <div
            #slideItem
            class="Slider-item"
            (click)="selectSlideItem(slideItemIndex, $event)"
          >
            <div class="Slide">
              <div class="Slide-inner">
                <fl-thumbnail-viewer-image
                  class="Slide-image"
                  [alt]="image.alt || ''"
                  [height]="imagesHeight"
                  [layout]="imagesLayout"
                  [resizeFit]="imagesResizeFit"
                  [src]="image.src"
                  [width]="imagesWidth"
                ></fl-thumbnail-viewer-image>
              </div>
            </div>
          </div>
        }
      </div>
    </div>
    @if (arrows) {
      <fl-link
        class="Navigator Navigator--next"
        [class.IsHidden]="hideNextArrow"
        (click)="onViewNext($event)"
      >
        <fl-icon
          i18n-label="Thumbnail viewer icon to view next"
          label="Next"
          [name]="'ui-arrow-right-alt'"
          [size]="IconSize.SMALL"
        ></fl-icon>
      </fl-link>
    }
  `,
  styleUrls: ['./thumbnail-viewer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ThumbnailViewerComponent
  implements OnChanges, AfterViewInit, OnDestroy
{
  IconSize = IconSize;

  trackWidth: number;
  slideWidth: number;
  slideIndex: number;
  currentGroupIndex = 0;
  currentIndexInGroup = 0;
  hidePrevArrow = false;
  hideNextArrow = false;
  maxImageCount = 0;

  @Input() images: readonly ThumbnailViewerImage[];
  @Input() slidesToShow = 1;
  @Input() currentIndex = 0;
  @Input() arrows = true;
  @Input() groupIndex = 0;
  @Input() slidesToScroll: ThumbnailViewerSlidesToScroll =
    ThumbnailViewerSlidesToScroll.INDIVIDUAL;

  @HostBinding('attr.data-arrow-position')
  @Input()
  arrowPosition = ThumbnailViewerArrowPosition.OUTSIDE;

  /**
   * The `PictureResizeFit` of each image to be displayed by the thumbnail viewer.
   * Both `imagesWidth` and `imagesHeight` is required to be set with a non-zero value when `imagesResizeFit` is provided.
   * And also, the layout of all `fl-picture`s in the component become `PictureLayout.FIXED`.
   *
   * Invalid usage of `imagesResizeFit` results in an error being thrown.
   */
  @Input() imagesResizeFit?: PictureResizeFit;
  /**
   * Width of space to be reserved in pixels. Passed directly to `fl-picture`'s `width` input
   */
  @Input() imagesWidth?: number;
  /**
   * Height of space to be reserved in pixels. Passed directly to `fl-picture`'s `height` input
   */
  @Input() imagesHeight?: number;

  @Output() slideChange = new EventEmitter<number>();
  @Output() scrollChange = new EventEmitter<number>();

  @ViewChild('track', { read: ElementRef }) track: ElementRef;
  @ViewChildren('slideItem', { read: ElementRef })
  slideItems: QueryList<ElementRef>;

  /**
   * FIXME: T277635 - Eventually resize fit should work with different layouts and we can specifiy a `sizes` input.
   */
  imagesLayout?: PictureLayout;

  private slideItemChangeSubscription?: Subscription;

  constructor(
    private renderer: Renderer2,
    private changeDetector: ChangeDetectorRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    this.maxImageCount = this.images.length - 1;

    // Prevents slideIndex from exceeding the images length
    this.slideIndex = Math.min(this.maxImageCount, this.currentIndex);

    // Prevents groupIndex from exceeding the images length
    this.currentGroupIndex = Math.min(
      this.groupIndex,
      this.getTotalGroupCount(),
    );

    if ('currentIndex' in changes && this.slideItems) {
      this.setActiveSlide();
      this.moveTrackPosition();
    }

    if ('slidesToShow' in changes && this.slideItems) {
      this.setViewWidths();
      this.moveTrackPosition();
    }

    if ('groupIndex' in changes && this.slideItems) {
      this.moveTrackPosition();
    }

    if ('images' in changes && this.slideItems) {
      this.moveTrackPosition();
    }

    if (
      'imagesResizeFit' in changes ||
      'imagesWidth' in changes ||
      'imagesHeight' in changes
    ) {
      if (this.imagesResizeFit) {
        if (!this.imagesWidth || !this.imagesHeight) {
          throw new Error(
            'Can only use imagesResizeFit when both imagesWidth and imagesHeight are set',
          );
        }
        this.imagesLayout = PictureLayout.FIXED;
      } else {
        this.imagesLayout = undefined;
      }
    }
  }

  ngAfterViewInit(): void {
    this.currentGroupIndex = this.groupIndex;
    this.setViewWidths();
    this.setActiveSlide();
    this.slideItemChangeSubscription = this.slideItems.changes.subscribe(() => {
      this.setViewWidths();
      this.setActiveSlide();
    });
    this.moveTrackPosition();
  }

  ngOnDestroy(): void {
    this.slideItemChangeSubscription?.unsubscribe();
  }

  setViewWidths(): void {
    this.setTrackWidth();
    this.setSlideItemWidth(this.images.length);
  }

  setTrackWidth(): void {
    this.trackWidth = (this.images.length / this.slidesToShow) * 100;

    this.renderer.setStyle(
      this.track.nativeElement,
      'width',
      `${this.trackWidth}%`,
    );
  }

  setSlideItemWidth(slideCount: number): void {
    this.slideWidth =
      this.trackWidth / (slideCount * (slideCount / this.slidesToShow));

    this.slideItems.forEach((slideItem: ElementRef) => {
      this.renderer.setStyle(
        slideItem.nativeElement,
        'width',
        `${this.slideWidth}%`,
      );
    });
  }

  setActiveSlide(): void {
    this.slideItems.forEach((slideItem: ElementRef, i) => {
      if (this.slideIndex === i) {
        this.renderer.addClass(slideItem.nativeElement, 'IsActive');
      } else {
        this.renderer.removeClass(slideItem.nativeElement, 'IsActive');
      }
    });
  }

  moveTrackPosition(): void {
    if (this.isIndividualScroll()) {
      this.currentGroupIndex = Math.floor(this.slideIndex / this.slidesToShow);
    }

    this.renderer.setStyle(
      this.track.nativeElement,
      'transform',
      `translateX(-${
        (this.currentGroupIndex / (this.trackWidth / 100)) * 100
      }%)`,
    );

    this.scrollChange.emit(this.currentGroupIndex);
    this.toggleArrows();
  }

  onViewPrevious(event: MouseEvent): void {
    event.stopPropagation();

    if (this.isIndividualScroll() && this.slideIndex >= 0) {
      this.slideIndex -= 1;
      this.setActiveSlide();
      this.slideChange.emit(this.slideIndex);
      this.moveTrackPosition();
    }

    if (!this.isIndividualScroll() && this.currentGroupIndex >= 0) {
      this.currentGroupIndex -= 1;
      this.moveTrackPosition();
    }
  }

  onViewNext(event: MouseEvent): void {
    event.stopPropagation();

    if (this.isIndividualScroll() && this.slideIndex < this.maxImageCount) {
      this.slideIndex += 1;
      this.setActiveSlide();
      this.slideChange.emit(this.slideIndex);
      this.moveTrackPosition();
    }

    if (
      !this.isIndividualScroll() &&
      this.currentGroupIndex < this.getTotalGroupCount()
    ) {
      this.currentGroupIndex += 1;
      this.moveTrackPosition();
    }
  }

  selectSlideItem(slideIndex: number, event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }

    if (slideIndex > this.maxImageCount || slideIndex < 0) {
      return;
    }

    this.slideIndex = slideIndex;
    this.setActiveSlide();
    this.moveTrackPosition();
    this.slideChange.emit(slideIndex);
  }

  toggleArrows(): void {
    this.hidePrevArrow =
      this.slidesToScroll === ThumbnailViewerSlidesToScroll.INDIVIDUAL
        ? this.slideIndex <= 0
        : this.currentGroupIndex <= 0;

    this.hideNextArrow =
      this.slidesToScroll === ThumbnailViewerSlidesToScroll.INDIVIDUAL
        ? this.slideIndex >= this.maxImageCount
        : this.currentGroupIndex >= this.getTotalGroupCount();

    this.changeDetector.markForCheck();
  }

  isIndividualScroll(): boolean {
    return this.slidesToScroll === ThumbnailViewerSlidesToScroll.INDIVIDUAL;
  }

  getTotalGroupCount(): number {
    return Math.floor(this.maxImageCount / this.slidesToShow);
  }

  trackByImageSource(_: number, image: ThumbnailViewerImage): string {
    return image.src;
  }
}
